<template>
  <EditionModal :is-displayed="!!problemId" :query-param="fwModalQueryParam" class="five-whys">
    <div ref="mainWrapper" class="five-whys__main-wrapper">
      <div v-if="problemId && stateProblem" class="five-whys__wrapper">
        <Stickers
          ref="stickers"
          :style="stickersStyle"
          @click="displayStickers"
          @closeClick="hideStickers"
        />
        <div class="f-h five-whys__causes">

          <div class="five-whys__hint">
            <div class="v-centerer">
              <div>
                {{ $t('help.W5_CAUSE_SELECT_HINT') }}
              </div>
              <button
                v-if="selectingCauses"
                class="generic-border generic-border--text-btn generic-border--gray-border"
                @click="hideCausesSelector()"
              >
                <div>{{ $t('message.CANCEL') }}</div>
              </button>
              <button
                v-else
                class="generic-border generic-border--text-btn generic-border--green-border"
                @click="showCausesSelection()"
              >
                <div>{{ $t('message.DISPLAY_IN_SHEET_SHORT') }}</div>
              </button>
            </div>
          </div>

          <div
            v-if="isMounted"
            ref="fwContent"
            class="five-whys__content"
            :style="fwContentStyle"
          >
            <FwSticker
              v-for="(sticker, stickerIndex) in stickers"
              :key="stickerIndex"
              :sticker="sticker"
              :problem="stateProblem"
              :fw-root="fwRoot"
            />
            <div class="w-tree-row">
              <FwCausesTree
                v-for="(cause, index) in cloneDeep(stateProblem.wCauses)"
                ref="children"
                :key="cause.id"
                :cause="cause"
                :selecting-causes="selectingCauses"
                :index="index"
                :fw-root="fwRoot"
                @edit="causeTreeEdit()"
              />
              <div
                ref="addBtn"
                class="generic-border generic-border--icon-button generic-border--gray-border five-whys__add"
                :style="addStyle"
                @click="addCause()"
              >
                <Icon>{{ addExtraBold }}</Icon>
              </div>
            </div>
          </div>
        </div>
        <div v-if="selectingCauses" ref="causeSelectionDimmer" class="five-whys__selection-overlay" />
      </div>
      <div ref="deleteZoneWrapper" class="five-whys__delete-zone v-centerer">
        <div
          ref="deleteZone"
          class="generic-border generic-border--text-btn generic-border--clickable generic-border--red-dashed-border"
        >
          <Icon>{{ deleteMedium }}</Icon>
          <span>{{ $t("message.DELETE") }}</span>
        </div>
      </div>
    </div>
  </EditionModal>
</template>

<script>
/**
 * 5 whys tool component.
 * Allows creating causes for a problem and managing them as a tree.
 */

import $ from 'jquery';
import { cloneDeep, isEqual } from 'lodash';
import toastr from 'toastr';
// Vuex
import { ADD_CAUSE, DELETE_CAUSE, EDIT_CAUSE } from '@/store/modules/problem/action-types';
// Import components
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import FwCausesTree from '@/components/fw-causes-tree.vue';
import FwSticker from '@/components/fw-sticker.vue';
import Icon from '@/components/icon.vue';
import Stickers from '@/components/stickers.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { calert, preventPastingStyles, detectEscapeKey, undetectEscapeKey } from '@/utils/main';
import { addExtraBold, deleteMedium, infosMedium } from '@/assets/icons';
import { fwModalQueryParam } from '@/utils/navigation-constants';
import IAHelpStep from '@/utils/classes/IAHelpStep';

function fiveWhysInitialData() {
  return {
    stickersDisplayed: false,
    // Edit cause
    selectingCauses: false,
    stickers: [],
    /** If true when calling init(), prevents it */
    preventReinit: false,
    // Measurements data
    measures: {
      container: null,
      tileMargin: 0,
      tileHeight: 0,
      tileWidth: 0,
      halfTileWidth: 0,
      addWidth: 0,
      addSidesMargin: 0,
      addTopMargin: 0,
    },
    isMounted: false,
  };
}

export default {

  name: 'FiveWhysEditionModal',

  components: {
    EditionModal,
    FwCausesTree,
    FwSticker,
    Icon,
    Stickers,
  },

  mixins: [mixinSocketHandler],

  data() {
    return {
      addExtraBold,
      deleteMedium,
      infosMedium,
      fwModalQueryParam,
      ...fiveWhysInitialData(),
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        this.$router.back();
      },
    };
  },

  computed: {
    /** Number: ID of the problem */
    problemId() {
      return this.$route.query[fwModalQueryParam] === undefined
        ? null
        : Number(this.$route.query[fwModalQueryParam]);
    },
    /** Problem as it was last set in the state */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.problemId}`];
    },
    stickersStyle() {
      return this.stickersDisplayed
        ? 'width: 20%; padding-top: 20%;'
        : 'width: 5%; padding-top: 5%;';
    },
    addStyle() {
      const noCauses = this.stateProblem && this.stateProblem.wCauses && this.stateProblem.wCauses.length === 0;
      return {
        margin: noCauses
          ? `${this.measures.addTopMargin}px ${this.measures.addSidesMargin}px`
          : `${this.measures.addTopMargin}px ${this.measures.tileMargin}px`,
      };
    },
    /** FW content need to be on top when selecting causes to allow scrolling */
    fwContentStyle() {
      if (this.selectingCauses) {
        return {
          zIndex: 10012,
          background: 'transparent',
        };
      }
      return '';
    },
    fwRoot() {
      return this;
    },
    // Properties used by other components. See src/views/readme.md
    pageTitle() {
      return this.stateProblem ? this.stateProblem.lWhat : this.$t('message.PROBLEM');
    },
  },

  watch: {
    stateProblem: {
      deep: true,
      handler(newVal, oldVal) {
        if (this.problemId !== null && newVal && !isEqual(newVal, oldVal)) {
          this.$nextTick(this.init);
        }
      },
    },
    problemId() {
      if (this.problemId !== null) {
        this.init();
      } else {
        Object.assign(this.$data, fiveWhysInitialData());
        $(this.$refs.deleteZoneWrapper).droppable('destroy');
        $(this.$refs.deleteZone).droppable('destroy');
      }
    },
  },

  created() {
    $(window).on(`resizeEnd.${this.uuid}`, this.init);
    this.handleSocketMessage(SocketMessages.CC_MSGS.STICKERS, (msg) => {
      if (Number(msg.id) !== Number(this.stateProblem.id)) return;
      this.refreshStickers();
    });
  },

  mounted() {
    if (this.problemId && this.stateProblem) {
      this.init();
    }
  },

  methods: {
    cloneDeep,
    /** Specify that an edit was made somewhere */
    anyEditMade() {
      preventPastingStyles();
    },
    /** Receive an edit event from the causes */
    causeTreeEdit() {
      this.diffuseEdit();
    },
    /** Diffuse edit event to children to redraw canvases */
    diffuseEdit() {
      setTimeout(() => {
        const { children } = this.$refs;
        if (children) {
          for (let i = 0, len = children.length; i < len; i++) {
            children[i].diffuseEdit();
          }
        }
      }, 1);
      this.anyEditMade();
    },
    /** Fetch stickers for this problem */
    refreshStickers() {
      if (this.stateProblem && this.stateProblem.id) {
        this.$http.post('/server/stickers/get.php', { problemId: this.stateProblem.id }).then(({ data }) => {
          if (Array.isArray(data)) {
            this.stickers = data;
          }
        });
      }
    },
    init() {
      if (this.preventReinit) {
        this.preventReinit = false;
        return;
      }
      if (!this.problemId || !this.stateProblem || !this.stateProblem.id) {
        return;
      }
      this.refreshStickers();

      this.measures.tileMargin = 7;
      this.measures.tileHeight = 150;
      this.measures.tileWidth = 220;
      this.measures.halfTileWidth = this.measures.tileWidth / 2;
      this.measures.addWidth = 40;
      this.measures.addSidesMargin = this.measures.tileWidth / 2
        - this.measures.addWidth / 2
        + this.measures.tileMargin;
      this.measures.addTopMargin = 30;
      this.isMounted = true;
      this.$nextTick(() => {
        this.diffuseEdit();
        this.measures.container = this.$refs.fwContent;
        this.setupDroppable();
      });
    },
    /** Create the droppable handlers */
    setupDroppable() {
      const container = this.$refs.fwContent.getBoundingClientRect();
      const topOffset = container.top;
      const leftOffset = container.left;
      // Allow dropping stickers there
      $(this.$refs.fwContent).droppable({
        accept: '.sticker .colored-sticker',
        drop: (e, ui) => {
          const xMult = 100 / this.$refs.fwContent.offsetWidth;
          const yMult = 100 / this.$refs.fwContent.offsetHeight;
          const sticker = ui.draggable[0];
          this.addSticker({
            id: 0,
            color: sticker.dataset.colIndex,
            problemId: this.stateProblem.id,
            rect: {
              x: (ui.offset.left - leftOffset + this.measures.container.scrollLeft) * xMult,
              y: (ui.offset.top - topOffset + this.measures.container.scrollTop) * yMult,
              width: this.measures.tileWidth * xMult,
              height: this.measures.tileHeight * yMult,
            },
          });
        },
      });
      // Manage deleting an item
      $(this.$refs.deleteZoneWrapper).droppable();
      $(this.$refs.deleteZone).droppable({
        accept: '.colored-sticker, .w-cause',
        tolerance: 'pointer',
        drop: (e, ui) => {
          const helper = ui.helper.length ? ui.helper[0] : ui.helper;
          const id = Number(helper.dataset.id);
          const { type } = helper.dataset;
          let deleteMsg = this.$t('message.CONFIRM_DELETE_STICKER');
          if (type === 'cause') {
            deleteMsg = this.$t('message.CONFIRM_DELETE_FW_CAUSE');
          }
          calert(deleteMsg, [
            {
              title: this.$t('message.DELETE'),
              action: () => {
                if (type === 'cause') {
                  this.deleteCause(id);
                } else {
                  this.deleteSticker(id);
                }
              },
              sClass: 'red',
            },
            { title: this.$t('message.CANCEL') },
          ]);
        },
      });

      // Manage dropping an item (sticker or cause) to append it
      $(this.$refs.addBtn).droppable({
        accept: '.five-whys__content .colored-sticker, .w-cause',
        tolerance: 'pointer',
        drop: (e, ui) => {
          const helper = ui.helper.length ? ui.helper[0] : ui.helper;
          const id = Number(helper.dataset.id);
          const { type } = helper.dataset;
          if (type === 'cause') {
            if (!this.stateProblem.wCauses.some(cause => cause.id === id)) {
              this.$store.dispatch(`problem/${EDIT_CAUSE}`, { id, problemId: this.problemId, cause: 0 })
                .then(() => {
                  toastr.remove();
                  toastr.success(this.$t('message.CAUSE_SAVED'));
                  this.sendSocketUpdate();
                });
            }
          } else {
            const stickerIdx = this.stickers.findIndex(sticker => sticker.id === id);
            if (stickerIdx >= 0) {
              this.addCause({ id: 0 }, this.stickers[stickerIdx].text);
              this.stickers.splice(stickerIdx, 1);
              this.deleteSticker(id);
            }
          }
        },
      });
    },
    /** Add a cause with no specific attribute */
    addCause(parent, text) {
      const params = { id: this.problemId, text, cause: parent ? parent.id : undefined, type: 1 };
      this.$store.dispatch(`problem/${ADD_CAUSE}`, params)
        .then(() => {
          preventPastingStyles();
          toastr.remove();
          toastr.success(this.$t('message.CAUSE_SAVED'));
          this.sendSocketUpdate();
        })
        .catch((msg) => {
          if (msg === -2) {
            calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
          } else {
            calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
          }
        });
    },
    /** Send the deletion XHR to the server */
    deleteCause(causeId) {
      this.$store.dispatch(`problem/${DELETE_CAUSE}`, { id: causeId })
        .then(() => {
          toastr.remove();
          toastr.success(this.$t('message.CAUSE_DELETED'));
          this.sendSocketMessage({ socketCompany: 1, id: this.problemId, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch((msg) => {
          if (msg === -2) {
            calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
          } else {
            calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
          }
        });
    },
    addSticker(sticker) {
      this.$http.post('/server/stickers/set.php', sticker).then(({ data }) => {
        if (Array.isArray(data)) {
          this.stickers = data;
          this.sendStickersSocketUpdate();
        }
      });
    },
    /** Send a socket msg to update the stickers of this problem sheet */
    sendStickersSocketUpdate() {
      this.sendSocketMessage({
        socketCompany: 1,
        id: this.stateProblem.id,
        msg: SocketMessages.CC_MSGS.STICKERS,
      });
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendProblemSocketUpdate() {
      this.sendSocketMessage({
        msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
        id: this.stateProblem.id,
        socketCompany: 1,
      });
    },
    /** Show a stickers container at the top right corner */
    displayStickers() {
      this.stickersDisplayed = true;
      this.$refs.stickers.enableDrag(this.$refs.fwContent);
    },
    /** Hide the stickers container */
    hideStickers() {
      this.stickersDisplayed = false;
      this.$refs.stickers.disableDrag();
    },
    /** Remove a sticker component */
    deleteSticker(stickerId) {
      const params = { id: stickerId, problemId: this.stateProblem.id };
      this.$http.post('/server/stickers/delete.php', params).then(({ data }) => {
        if (Array.isArray(data)) {
          this.stickers = data;
          this.sendStickersSocketUpdate();
        }
      });
    },
    /** Allow user to select causes which will apear on the problem sheet */
    showCausesSelection() {
      this.selectingCauses = true;
      detectEscapeKey(() => {
        this.hideCausesSelector();
      });
    },
    hideCausesSelector(undetect) {
      this.selectingCauses = false;
      if (undetect) {
        undetectEscapeKey();
      }
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({
        msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
        id: this.problemId,
        socketCompany: 1,
      });
    },
    createHelpSteps() {
      return [
        new IAHelpStep(null, this.$t('help.W5_INTRO')),
        new IAHelpStep('.w-cause', this.$t('help.W5_CAUSE')),
        new IAHelpStep('.w-tree .generic-border--icon-button', this.$t('help.W5_CAUSE_DROP'), () => {
          this.showCausesSelection();
        }),
        new IAHelpStep(null, this.$t('help.W5_CAUSE_SELECT'), () => {
          this.hideCausesSelector(true);
        }),
      ];
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.five-whys {

  &__main-wrapper {
    display: flex;
    flex-flow: column;
    height: 100%;
  }

  &__wrapper {
    margin: $page-content-padding;
    width: calc(100% - calc(2 * #{$page-content-padding}));
    background: white;
    border-radius: $main-border-radius;
    flex: 1;
    min-height: 0px;

    .w-stickers {
      position: absolute;
      right: 20px;
      top: 80px;
      z-index: 10009;/* Must stay below #cause-sel-dimmer */
      transition: width 0.2s ease-in-out, padding 0.2s ease-in-out;
    }
  }

  &__hint {
    background: $color-shizen-light-blue-hover;
    padding: 1rem;
    border-radius: $main-border-radius;
    flex: 0 0 auto;

    .icon {
      margin-right: 1rem;
      flex: 0 0 auto;
      width: 1.2rem;
      height: 1.2rem;
    }

    button.generic-border--text-btn {
      margin-left: 1rem;
      background: white;
    }
  }

  &__causes {
    padding: $page-content-padding;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    overflow: auto;
    // Above the selection overlay
    z-index: 2;
    position: relative;
    display: flex;
    flex-flow: column;
  }

  &__content {
    margin-top: 1rem;
    position: relative;
    flex: 1;
  }

  .w-tree-row {
    white-space: nowrap;

    > * {
      display: inline-block;
      vertical-align: top;

      &.generic-border--icon-button {
        display: inline-flex;
      }
    }
  }

  &__add {
    &.ui-droppable-active.generic-border--gray-border {
      border: dashed 3px $color-shizen-blue-btn-shadow-color;
    }
    &.ui-droppable-hover.generic-border--gray-border {
      border: solid 3px $color-shizen-blue-btn-shadow-color;
      background: $color-shizen-blue-btn-shadow-color;
    }
  }

  &__delete-zone {
    flex: 0 0 auto;
    background: #fff;
    height: 0px;
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    overflow: hidden;

    &.ui-droppable-active {
      height: $buttons-zone-height;
      padding: $buttons-zone-padding;
    }

    .generic-border--red-dashed-border {
      font-weight: 600;

      &.ui-droppable-hover {
        background: $color-shizen-red;
        border-style: solid;
        color: white;
      }
    }
  }

  &__selection-overlay {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    z-index: 1;
    background: rgba(0,0,0,0.3);
    text-align: center;
    padding: 20px;
  }
}
</style>
